/**
 * "This deck just came back on screen" — the signal a mount-once fetch needs.
 *
 * ── The bug this exists for ─────────────────────────────────────────────
 *
 * The desktop shell does NOT unmount a deck when you switch away from it. Every
 * deck renders as `{(shell.isRunning(k) || deckKind === k) && …}` with
 * `style={{ display: deckKind === k ? undefined : 'none' }}`, so once Settings
 * has been opened its whole React tree stays mounted for the life of the tab and
 * merely stops being painted. That is deliberate — it is what makes deck
 * switching instant and preserves scroll, drafts and open panels.
 *
 * The consequence is that a page whose data arrives from a `useEffect(…, [])`
 * fetches ONCE, ever. Settings → Spending was the reported symptom: use the AI
 * assistant, come back to Spending, and the figures are whatever they were the
 * first time the deck was opened — correct-looking, no error, and only a full
 * page reload fixes it. Reported 2026-08-13.
 *
 * Mobile is unaffected: `MobileShell` mounts these screens as real routes, so
 * navigation genuinely unmounts and remounts them. Nothing here changes that —
 * with no provider above it the hook is inert, which is exactly right.
 *
 * ── Why a context and not `document.visibilitychange` ───────────────────
 *
 * The tab never lost focus. Switching decks is an in-page navigation the browser
 * knows nothing about, so no document-level event fires. And it is not a router
 * event either: the deck's own `<Routes>` are still mounted and matched, they
 * simply live inside a hidden subtree.
 *
 * ── Why a counter and not a boolean ─────────────────────────────────────
 *
 * A boolean `visible` prop would make consumers write
 * `useEffect(() => { if (visible) refresh() }, [visible])`, which fires on the
 * FIRST show too — duplicating the mount fetch every consumer already has. The
 * counter starts at 0 and only increments on a hidden→shown TRANSITION, so
 * "shown for the first time" is 0 and costs nothing.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

/**
 * How many times this deck has been RE-shown. `0` = never hidden yet, which is
 * also the value outside any provider — so a component rendered on mobile, in a
 * test, or anywhere else with no deck above it simply never re-fetches, and
 * behaves exactly as it did before this existed.
 */
const Ctx = createContext<number>(0)

/**
 * Is this subtree on screen right now? `true` outside any provider (mobile,
 * tests, a dialog) — and inside nested providers only when every one is active.
 */
const ShownCtx = createContext<boolean>(true)

/**
 * Wrap a deck's subtree. `active` is whatever the shell already computes to
 * decide `display: none` — pass the same expression, so the signal cannot
 * disagree with what the user sees.
 */
export function DeckVisibility({ active, children }: { active: boolean; children: ReactNode }) {
  const [shows, setShows] = useState(0)
  const wasActive = useRef(active)

  useEffect(() => {
    // Guard on the PREVIOUS value, not on `active` alone. `<StrictMode>` is on
    // in dev and double-invokes effects (mount → cleanup → mount); a version
    // that checked only `if (active)` would count the second run as a second
    // show and fire every consumer's refresh twice on every switch. Comparing
    // against the ref makes a repeated run a no-op, which is the rule CLAUDE.md
    // states after `ActionDisclosure` shipped with the other shape.
    if (active && !wasActive.current) setShows(n => n + 1)
    wasActive.current = active
  }, [active])

  const parentShown = useContext(ShownCtx)
  return <ShownCtx.Provider value={parentShown && active}><Ctx.Provider value={shows}>{children}</Ctx.Provider></ShownCtx.Provider>
}

/** Is the enclosing deck on screen now? (`true` with no deck above.) */
export function useScreenShown(): boolean {
  return useContext(ShownCtx)
}

/** `useWhenShown`'s runner: call it with a reaction; `cancel()` drops one still waiting, `pending()` says whether one is. */
export type WhenShown = ((reaction: () => void) => void) & { cancel: () => void; pending: () => boolean }

/**
 * Run a reaction now if the enclosing deck is on screen, else ONCE when it is
 * shown again (#1259) — the latest one asked for wins, earlier ones are
 * dropped. For live reactions to other people's changes (an access change, a
 * grant edit): a hidden deck or cached workspace used to re-read everything on
 * every change while nobody looked at it. Dropped on unmount — what it would
 * have re-read must already be marked stale by whoever owns that cache.
 *
 * A reaction that closes over something that can change (a folder id) must be
 * dropped when that changes: call `cancel()` from the same effect cleanup that
 * cancels its burst, or the old folder's reaction runs when the deck is shown.
 *
 * The returned function is stable; call it from any callback.
 */
export function useWhenShown(): WhenShown {
  const shown = useContext(ShownCtx)
  const shownRef = useRef(shown)
  const pending = useRef<(() => void) | null>(null)
  useLayoutEffect(() => { shownRef.current = shown })
  useEffect(() => {
    if (!shown || !pending.current) return
    const run = pending.current
    pending.current = null
    run()
  }, [shown])
  useEffect(() => () => { pending.current = null }, [])
  const [runner] = useState<WhenShown>(() => Object.assign(
    (reaction: () => void) => {
      if (shownRef.current) { pending.current = null; reaction() } else pending.current = reaction
    },
    { cancel: () => { pending.current = null }, pending: () => pending.current !== null },
  ))
  return runner
}

/**
 * Re-run `refresh` each time the enclosing deck is shown again.
 *
 * Does NOT fire on first mount — the consumer's own mount effect already
 * fetched, and firing here too would double every page's initial load.
 *
 * `refresh` is read through a ref, so a caller may pass an inline closure
 * without the effect re-running on every render. That matters: the natural call
 * site is `useRefreshWhenShown(() => { void load() })` inside a component that
 * re-renders on every keystroke in its own filter box.
 */
export function useRefreshWhenShown(refresh: () => void): void {
  const shows = useContext(Ctx)
  const fn = useRef(refresh)
  fn.current = refresh
  const seen = useRef(shows)

  useEffect(() => {
    if (shows === seen.current) return
    seen.current = shows
    fn.current()
  }, [shows])
}
